import { InviteStatus, getInviteStatus, getInviteStatusTooltip } from "@utils/providerInviteStatus";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { Badge, Button, Column, Small, Table, Tooltip } from "@fronterahealth/frontera-ui-components";

import { ApiProviderMetadataInvitationStatusChoices, ProviderType } from "@api/graphql/types-and-hooks";
import { TableFilterDropdown } from "@components/TableFilterDropdown/TableFilterDropdown";
import { UserInitialsView } from "@components/UserInitial/UserInitialsView";
import { InviteProviderModal } from "@pages/Members/components/InviteProviderModal";
import { MemberProvider } from "@pages/Members/types";
import { mapProviderToMemberProvider } from "@pages/Members/utils/providerMapping";
import { useAdminData } from "@providers/AdminDataProvider";
import { useProvidersData } from "@providers/ProvidersDataProvider";

const portalStatusBadge = (status: ApiProviderMetadataInvitationStatusChoices | null) => {
  const badge = (() => {
    switch (status) {
      case ApiProviderMetadataInvitationStatusChoices.Accepted:
        return <Badge appearance="success" text="Active" />;
      case ApiProviderMetadataInvitationStatusChoices.Pending:
      case ApiProviderMetadataInvitationStatusChoices.Expired:
        return <Badge appearance="warn" text="Invited" />;
      case null:
        return <Badge appearance="neutral" text="Not invited" />;
    }
  })();

  const inviteStatus = getInviteStatus(status ?? "");
  return (
    <Tooltip
      showInfoIcon={false}
      disabled={inviteStatus !== InviteStatus.NotInvited && inviteStatus !== InviteStatus.Pending}
      text={getInviteStatusTooltip(inviteStatus)}
    >
      {badge}
    </Tooltip>
  );
};

// TableFilterDropdown requires string ids, so "not invited" (which is `null` on the record itself) needs a string stand-in here.
type StatusFilterId = ApiProviderMetadataInvitationStatusChoices | "not_invited";

const STATUS_FILTER_ITEMS: { id: StatusFilterId; label: string }[] = [
  { id: "not_invited", label: "Not invited" },
  { id: ApiProviderMetadataInvitationStatusChoices.Pending, label: "Invited" },
  { id: ApiProviderMetadataInvitationStatusChoices.Accepted, label: "Active" },
];

const matchesStatusFilter = (
  status: ApiProviderMetadataInvitationStatusChoices | null,
  filterId: StatusFilterId,
): boolean => {
  switch (filterId) {
    case "not_invited":
      return status === null;
    case ApiProviderMetadataInvitationStatusChoices.Pending:
      return (
        status === ApiProviderMetadataInvitationStatusChoices.Pending ||
        status === ApiProviderMetadataInvitationStatusChoices.Expired
      );
    default:
      return status === filterId;
  }
};

interface ProvidersTabProps {
  buttonContainer: HTMLDivElement | null;
}

export const ProvidersTab: React.FC<ProvidersTabProps> = ({ buttonContainer }) => {
  const navigate = useNavigate();
  const { roles } = useAdminData();
  const isViewerFronteraInternal = roles.includes("frontera-internal");
  const { providersQuery } = useProvidersData();
  const { data, isFetching } = providersQuery;

  const [statusFilter, setStatusFilter] = useState<StatusFilterId[]>([]);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const providers = useMemo(
    () =>
      ((data?.provider || []) as ProviderType[])
        .map(mapProviderToMemberProvider)
        .filter((p): p is MemberProvider => p !== null),
    [data],
  );

  const filteredProviders = providers.filter(
    (provider) =>
      statusFilter.length === 0 ||
      statusFilter.some((filterId) => matchesStatusFilter(provider.portalStatus, filterId)),
  );

  const columns: Column<MemberProvider>[] = [
    {
      dataIndex: "name",
      key: "name",
      title: "Name",
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <UserInitialsView
            firstName={record.firstName}
            lastName={record.lastName}
            className="!h-8 !w-8 !max-h-8 !max-w-8 text-xs"
          />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <Small displayType="loud" className="text-text-primary">
                {record.name}
              </Small>
              {isViewerFronteraInternal && record.hasInternalEmail && (
                <Tooltip
                  text="This provider's email indicates that they are an internal employee so they are not visible to non-Frontera-internal admins"
                  showInfoIcon={false}
                >
                  <Badge
                    appearance="warn"
                    text="Internal Email"
                    aria-label="Hidden from non-Frontera-internal admins"
                  />
                </Tooltip>
              )}
            </div>
            <Small colorType="secondary" className="truncate text-xs">
              {record.email || "No email"}
            </Small>
          </div>
        </div>
      ),
    },
    {
      dataIndex: "roles",
      key: "roles",
      title: "Role",
      render: (_, record) => (
        <div className="flex items-center gap-1.5">
          <Small>{record.roles[0] || "—"}</Small>
          {record.roles.length > 1 && (
            <Tooltip
              text={record.roles.slice(1).map((role) => (
                <Small key={role} className="text-white">
                  {role}
                </Small>
              ))}
              showInfoIcon={false}
            >
              <Badge text={`+${record.roles.length - 1}`} appearance="neutral" />
            </Tooltip>
          )}
          {isViewerFronteraInternal && record.isFronteraInternal && (
            <Tooltip
              text="This provider is an internal employee and is not visible to non-Frontera-internal admins"
              showInfoIcon={false}
            >
              <Badge
                appearance="warn"
                text="Frontera Internal Only"
                aria-label="Hidden from non-Frontera-internal admins"
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      dataIndex: "assignedClientsCount",
      key: "assignedClientsCount",
      title: "Clients",
      render: (_, record) => <Small>{record.assignedClientsCount}</Small>,
    },
    {
      dataIndex: "portalStatus",
      key: "portalStatus",
      title: "Portal Access",
      render: (_, record) => portalStatusBadge(record.portalStatus),
    },
  ];

  const handleRowClick = (provider: MemberProvider) => {
    navigate(`/members/provider/${provider.id}`);
  };

  const actionButtons = (
    <>
      <Button
        text="Invite Provider"
        variant="primary"
        onClick={() => {
          setInviteModalOpen(true);
        }}
      />
    </>
  );

  return (
    <div className="w-full">
      {buttonContainer && createPortal(actionButtons, buttonContainer)}

      {/* Table */}
      <div className="mt-4 min-h-[420px] w-full [&>div>div>div]:overflow-visible">
        <Table
          onRowClick={handleRowClick}
          defaultItemsPerPage={10}
          showPagination
          showSearch
          loading={isFetching}
          itemsPerPageOptions={[5, 10, 25]}
          data={filteredProviders}
          columns={columns}
          headerContent={
            <TableFilterDropdown<StatusFilterId>
              label="Status"
              items={STATUS_FILTER_ITEMS}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          }
        />
      </div>

      <InviteProviderModal isOpen={inviteModalOpen} onClose={() => setInviteModalOpen(false)} />
    </div>
  );
};
